/* eslint-disable max-len */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ReferenceKey } from '@1po/1po-bff-fe-spec/generated/catalog/references/common/model/ReferenceKey';
import { CatalogSource } from '@1po/1po-bff-fe-spec/generated/catalog/search_vehicle/response/VehicleSearchHistoryResponse';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { Popover } from 'antd';
import { TFunction } from 'i18next';
import { Dispatch } from 'redux';
import { useDebouncedCallback } from 'use-debounce';
import { ROUTER_CART, ROUTER_CATALOG_VEHICLE, ROUTER_PRODUCT } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { CarIcon, DepositIcon, LinkIcon, TrashAltIcon, UnlinkIcon } from 'assets/icons';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import { Dialog, useDisclosure } from 'components/Dialog';
import {
  calculatePriceAfterDiscount,
  calculateUrgentDeliveryAddition,
  DiscountCorner,
  getPromotionColor,
} from 'components/Discount/Discount';
import { FirstHelpPopin } from 'components/Help/FirstHelpPopin';
import QuantityBox from 'components/QuantityBox';
import { PriceErrorDisplay } from 'components/ReferencePriceSection';
import StockDisplay from 'components/StockInfo';
import {
  getOilChunk,
  getTechnicalCharacteristicsValue,
  isBulkCertasOil,
  isCanCertasOil,
  isNonBulkCertasOil,
} from 'components/StockInfo/catalogStockUtils';
import { useIsBulkOilUser, useIsOilChunkUserR1 } from 'domains/appContext/AppContext.requests';
import {
  attachOtherReferenceToVehicleRequest,
  clearActionStatuses,
  getBasketExternalSection,
  getBasketId,
  getBasketOtherSection,
  getBasketVehicles,
  removeSingleReferenceRequest,
  setBasket,
  setFileReferencesUploadStatusErrorRows,
  setReferencesIsSelected,
  updateReferenceMarkRequest,
  updateReferenceQuantity,
  updateReferenceQuantityRequest,
  updateReferenceUrgencyFlag,
  updateUrgencyFlagRequest,
} from 'domains/basket/Basket.store';
import {
  BULK_OIL_REFERENCE_MINIMAL_QUANTITY_LIMIT,
  CERTAS_OIL_REFERENCE_QUANTITY_LIMIT,
  ExternalBasketSectionLocal,
  OtherSectionLocal,
  ReferenceLocal,
  VehicleLocal,
} from 'domains/basket/Basket.types';
import { CERTAS_OIL_TECHNICAL_CHARACTERISTICS_CHUNK_CODE, IAM, STANDARD } from 'domains/catalog/Catalog.types';
import { getCatalogSourceUrl } from 'domains/catalog/Catalog.utils';
import { getDHReference, getDiscount, getPrice, getStockInfo } from 'domains/references';
import {
  checkSelectedHandler,
  getDepositMainReferenceNumber,
  getMainReferenceFromDeposit,
  isRefNumberDeposit,
  isRefNumberDepositMain,
} from 'domains/references/DepositReference';
import { getCurrency, getUserCommercialLink, getUserCountry } from 'domains/user';
import { tireBrandsData } from 'pages/TiresPage/TireSearch/TiresSearchbar/TireBrandsData';
import {
  Box,
  Checkbox,
  ColoredText,
  Flex,
  Icon,
  IconButton,
  Link,
  MarginBox,
  notifyTop,
  PencilRoundButton,
  Spin,
  Text,
  TrashAltButton,
  WithTooltip,
} from 'UI';
import { FOUND, getData, hasData, isQueryBMM, NOT_FOUND } from 'utils';
import { textFormatter } from 'utils/format';
import { formatVehicleBrand } from 'utils/mappers';
import { getMaxOrderQuantityForFamily, isSingleUnitOrderRestrictedFamily } from 'utils/rights';
import {
  AttachToVehicleContent,
  AttachToVehicleHeader,
  ExternalStrip,
  OverlayBox,
  RefInput,
  SPopover,
  STable,
  TireNameContainer,
  TooltipContent,
} from './CartReferenceTable.styled';

const getBasketReferenceType = (vehicleKey: string | undefined, externalBasketId: string | undefined) => {
  return vehicleKey ? 'VEHICLE' : externalBasketId ? 'EXTERNAL_BASKET' : 'OTHER';
};

export const calculateReferenceChunk = (
  reference: ReferenceLocal,
  stock: { data?: { warehouses: Array<{ type: string; confirmedQuantity: number }> } } | undefined,
  isCanOilReference: boolean | undefined,
  isOilReference: boolean | undefined,
  fallbackChunk?: string,
): number | undefined => {
  if (!isCanOilReference && stock?.data?.warehouses.some((wh) => wh.type === 'LOCAL' && wh.confirmedQuantity > 0)) {
    return 1;
  }
  return isOilReference ? getOilChunk(reference, fallbackChunk) : undefined;
};

const Stock = ({
  referenceNumber,
  reference,
  vehicleKey,
  externalBasketId: _externalBasketId,
  disabledRefs,
  setDisableRefs,
  unsetDisableRef,
  sellerNumber,
}: {
  referenceNumber: string;
  reference: ReferenceLocal;
  vehicleKey: string | undefined;
  externalBasketId: string | undefined;
  disabledRefs: string[];
  setDisableRefs: (ref: string) => void;
  unsetDisableRef: (ref: string) => void;
  sellerNumber: string | undefined;
}) => {
  const stockInfo = useSelector((state: RootState) => getStockInfo(state, referenceNumber, sellerNumber));
  const noStockData = stockInfo?.searchStatus === NOT_FOUND;
  // Keep freshly loaded basket rows selected until stock resolution finishes.
  // Otherwise, CSV-uploaded IAM rows flicker to unchecked while their stock request is still pending.
  const outOfStock = stockInfo?.searchStatus === FOUND && (stockInfo.data?.confirmedQuantity ?? 0) < 1;
  const shouldDisable = noStockData || (outOfStock && reference.brand === 'IAM');
  useEffect(() => {
    if (shouldDisable && !disabledRefs.includes(referenceNumber)) {
      setDisableRefs(referenceNumber);
    } else if (!shouldDisable && disabledRefs.includes(referenceNumber)) {
      unsetDisableRef(referenceNumber);
    }
  }, [shouldDisable, disabledRefs, referenceNumber, setDisableRefs, unsetDisableRef]);

  return (
    <Flex>
      <StockDisplay
        vehicleKey={vehicleKey}
        referenceNumber={referenceNumber}
        isApplicableToCurrentVehicle
        sellerNumber={sellerNumber}
        noPviInfo
        narrow
      />
    </Flex>
  );
};

const Selected = ({
  isSelected,
  reference,
  vehicleKey,
  externalBasketId,
  disabledRefs: _disabledRefs,
}: {
  isSelected: boolean;
  reference: ReferenceLocal;
  vehicleKey: string | undefined;
  externalBasketId: string | undefined;
  disabledRefs: string[];
}) => {
  const dispatch = useDispatch();
  const vehicles = useSelector(getBasketVehicles);
  const otherSection = useSelector(getBasketOtherSection);
  const externalBaskets = useSelector(getBasketExternalSection);
  const basketId = useSelector(getBasketId);
  const isBulkOilUser = useIsBulkOilUser() ?? false;

  const handleCheckChange = () => {
    if (vehicleKey) {
      const thisVehicle = vehicles?.find((vehicle) => vehicle?.vehicleDetail?.vehicleKey === vehicleKey);
      const newThisVehicle = {
        ...thisVehicle,
        references: checkSelectedHandler(
          {
            referenceNumber: reference.referenceNumber,
            sellerNumber: reference.sellerNumber,
          },
          thisVehicle?.references,
        ),
      } as VehicleLocal;
      const newVehicles = vehicles?.map((vehicle) =>
        vehicle?.vehicleDetail?.vehicleKey === vehicleKey ? newThisVehicle : vehicle,
      );

      dispatch(
        setBasket({
          basketId: basketId ?? '',
          vehicles: newVehicles,
          otherSection,
          externalBaskets,
          isBulkOilUser,
        }),
      );
    } else if (externalBasketId) {
      const thisExternal = externalBaskets?.find((extBasket) => extBasket.externalBasketId === externalBasketId);
      const newThisExternal = {
        ...thisExternal,
        references: checkSelectedHandler(
          {
            referenceNumber: reference.referenceNumber,
            sellerNumber: reference.sellerNumber,
          },
          thisExternal?.references,
        ),
      } as ExternalBasketSectionLocal;
      const newExternals = externalBaskets?.map((external) =>
        external.externalBasketId === externalBasketId ? newThisExternal : external,
      );

      dispatch(
        setBasket({
          basketId: basketId ?? '',
          externalBaskets: newExternals,
          vehicles,
          otherSection,
          isBulkOilUser,
        }),
      );
    } else {
      const newRef =
        checkSelectedHandler(
          {
            referenceNumber: reference.referenceNumber,
            sellerNumber: reference.sellerNumber,
          },
          otherSection?.references,
        ) ?? [];
      const newOther: OtherSectionLocal = {
        orderMark: otherSection?.orderMark ?? '',
        references: newRef,
      };

      dispatch(
        setBasket({
          basketId: basketId ?? '',
          otherSection: newOther,
          vehicles,
          externalBaskets,
          isBulkOilUser,
        }),
      );
    }
  };

  return (
    <Checkbox
      checked={isSelected}
      onChange={handleCheckChange}
      data-cy={`cart-reference-checkbox-${reference.referenceNumber}-${reference.sellerNumber ?? 'no-seller'}`}
    />
  );
};

export const BasketQuantityInput = ({
  currentQuantity,
  vehicleKey,
  externalBasketId,
  reference,
  markRemovedReferences,
}: {
  currentQuantity: number;
  vehicleKey: string | undefined;
  externalBasketId: string | undefined;
  reference: ReferenceLocal;
  markRemovedReferences: (numberOfReferences: number) => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const stock = useSelector((state: RootState) => getStockInfo(state, reference.referenceNumber));
  const dhReference = useSelector((state: RootState) =>
    getDHReference(state, { vehicleKey, referenceNumber: reference.referenceNumber }),
  );
  const dhReferenceFallback = useSelector((state: RootState) =>
    getDHReference(state, { vehicleKey: undefined, referenceNumber: reference.referenceNumber }),
  );
  const dhReferenceFallbackData = hasData(dhReferenceFallback) ? dhReferenceFallback : undefined;
  const dhReferenceData = hasData(dhReference) ? dhReference : dhReferenceFallbackData;
  const dhChunk = getTechnicalCharacteristicsValue(dhReferenceData, CERTAS_OIL_TECHNICAL_CHARACTERISTICS_CHUNK_CODE);
  const isOilUser = useIsOilChunkUserR1();
  const isBulkOilUser = useIsBulkOilUser();

  // Resolve Certas oil fields from basket reference with DH reference fallback
  const resolvedFamilyCode = reference.familyCode ?? dhReferenceData?.familyCode;
  const resolvedProductModelCode = reference.productModelCode ?? dhReferenceData?.productModelCode;
  const resolvedPackaging = reference.packaging ?? dhReferenceData?.packaging;

  const isOilReference =
    isOilUser && isNonBulkCertasOil(resolvedFamilyCode, resolvedProductModelCode, resolvedPackaging);
  const isBulkOilReference =
    isBulkOilUser && isBulkCertasOil(resolvedFamilyCode, resolvedProductModelCode, resolvedPackaging);
  const isCanOilReference =
    isOilUser && isCanCertasOil(resolvedFamilyCode, resolvedProductModelCode, resolvedPackaging);
  const isSingleUnitRestrictedReference = isSingleUnitOrderRestrictedFamily(resolvedFamilyCode);

  const onChangeQuantity = useCallback(
    (quantity: number | string) => {
      if (quantity === 0) {
        markRemovedReferences(1);
        dispatch(
          removeSingleReferenceRequest({
            vehicleKey,
            externalBasketId,
            referenceKey: {
              referenceNumber: reference.referenceNumber,
              sellerNumber: reference.sellerNumber,
            },
            basketReferenceType: getBasketReferenceType(vehicleKey, externalBasketId),
          }),
        );
      } else {
        const newQuantity =
          (reference.origin === 'IAM' || reference.referenceSource === 'MARKETPLACE') &&
          stock?.data?.confirmedQuantity !== undefined &&
          stock?.data?.confirmedQuantity > 0 &&
          stock?.data?.confirmedQuantity <= (quantity as number) &&
          reference.quantity >= (quantity as number)
            ? stock?.data?.confirmedQuantity
            : (quantity as number);

        dispatch(
          updateReferenceQuantityRequest({
            vehicleKey,
            externalBasketId,
            newQuantity,
            referenceToUpdate: {
              referenceNumber: reference.referenceNumber,
              sellerNumber: reference.sellerNumber,
            },
            basketReferenceType: getBasketReferenceType(vehicleKey, externalBasketId),
            origin: reference.origin,
            supplierCode: reference.supplierCode,
            referenceSource: reference.referenceSource,
            referenceType: reference.type ?? STANDARD,
            isBulkOilReference: isBulkCertasOil(resolvedFamilyCode, resolvedProductModelCode, resolvedPackaging),
          }),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, externalBasketId, markRemovedReferences, reference.referenceNumber, vehicleKey, stock],
  );

  const onMaxValueOverflow = useCallback(() => {
    if (stock?.data?.maxConfirmedQuantity !== undefined) {
      dispatch(
        updateReferenceQuantity({
          externalBasketId,
          vehicleKey,
          referenceToUpdate: {
            referenceNumber: reference.referenceNumber,
            sellerNumber: reference.sellerNumber,
          },
          newQuantity: isOilReference ? (getMaxQtyForOilInR1Stock() as number) : stock?.data?.maxConfirmedQuantity,
        }),
      );
    }
    const fallbackMessage = isSingleUnitRestrictedReference
      ? t(
          'cart.action.add_reference.traction_battery_single_unit_tooltip',
          'This part is restricted to single-unit orders.',
        )
      : t(
          'catalog.reference.stock.notification.parts_unavailable',
          'Sorry, Looks like the desired number of parts is currently unavailable.',
        );

    notifyTop('info', fallbackMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    externalBasketId,
    isOilReference,
    isSingleUnitRestrictedReference,
    reference.referenceNumber,
    reference.sellerNumber,
    stock?.data?.maxConfirmedQuantity,
    t,
    vehicleKey,
  ]);

  const getChunk = useCallback(() => {
    return calculateReferenceChunk(reference, stock, isCanOilReference, isOilReference, dhChunk);
  }, [reference, stock, isCanOilReference, isOilReference, dhChunk]);

  const getMaxQtyForOilInR1Stock = () => {
    return getMaxOrderQuantityForFamily(
      resolvedFamilyCode,
      isBulkOilReference || isOilReference ? CERTAS_OIL_REFERENCE_QUANTITY_LIMIT : stock?.data?.maxConfirmedQuantity,
    );
  };

  const getCurrentQuantityLocal = () => {
    if (isCanOilReference) {
      return Math.floor(currentQuantity / getOilChunk(reference, dhChunk)) * getOilChunk(reference, dhChunk);
    } else {
      return currentQuantity;
    }
  };

  return (
    <QuantityBox
      value={getCurrentQuantityLocal()}
      onChange={onChangeQuantity}
      maxValue={getMaxQtyForOilInR1Stock()}
      minValue={isBulkOilReference ? BULK_OIL_REFERENCE_MINIMAL_QUANTITY_LIMIT : 1}
      onMaxValueOverflow={onMaxValueOverflow}
      isSingleUnitRestricted={isSingleUnitRestrictedReference}
      chunk={getChunk()}
      isMinBulkChunk={isBulkOilReference}
      dataCy={'cart-quantity-input'}
    />
  );
};

function TrashButton({
  referenceNumber,
  vehicleKey,
  externalBasketId,
  markRemovedReferences,
  sellerNumber,
}: Readonly<{
  referenceNumber: string;
  markRemovedReferences: (numberOfReferences: number) => void;
  vehicleKey: string | undefined;
  externalBasketId: string | undefined;
  sellerNumber: string | undefined;
}>) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const vehicles = useSelector(getBasketVehicles);

  // ToDo - refactor to the global store
  const referenceCount = vehicles
    ?.find((vehicle) => vehicle.vehicleDetail.vehicleKey === vehicleKey)
    ?.references.filter((r) => !isRefNumberDeposit(r.referenceNumber))?.length;

  const disclosure = useDisclosure();
  const { onOpen } = disclosure;

  const handleConfirm = () => {
    dispatch(clearActionStatuses());
    dispatch(setFileReferencesUploadStatusErrorRows([]));
    dispatch(
      removeSingleReferenceRequest({
        basketReferenceType: getBasketReferenceType(vehicleKey, externalBasketId),
        vehicleKey,
        externalBasketId,
        referenceKey: { referenceNumber, sellerNumber },
      }),
    );
    markRemovedReferences(1);
  };

  function handleRemoveButton() {
    dispatch(
      setReferencesIsSelected({
        isSelected: true,
        referenceIds: [{ referenceNumber, sellerNumber }],
        vehicleKey,
      }),
    );
    if (vehicleKey && referenceCount === 1) {
      onOpen();
    } else {
      dispatch(
        removeSingleReferenceRequest({
          basketReferenceType: getBasketReferenceType(vehicleKey, externalBasketId),
          vehicleKey,
          externalBasketId: externalBasketId,
          referenceKey: { referenceNumber, sellerNumber },
        }),
      );
      markRemovedReferences(1);
    }
  }

  return (
    <div id={'trash_button_select'}>
      <TrashAltButton
        onClick={handleRemoveButton}
        data-cy={`cart-reference-trash-button-${referenceNumber}-${sellerNumber ?? 'no-seller'}`}
      />
      <Dialog
        disclosure={disclosure}
        title={t('common.dialog.actionRequired', 'Action required')}
        description={[
          t('cart.action.removalText1', 'Deleting this reference will delete your vehicle basket.'),
          t('common.dialog.confirmationQuestion', 'Would you like to confirm?'),
        ]}
        icon={TrashAltIcon}
        status={'info'}
        handleConfirm={handleConfirm}
      />
    </div>
  );
}

const AttachToVehicle = ({ vehicles, reference }: { vehicles: VehicleLocal[]; reference: ReferenceKey }) => {
  const [activeSelecting, setActiveSelecting] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  function handleAttach(vehicleKey: string) {
    dispatch(attachOtherReferenceToVehicleRequest({ vehicleKey, reference }));
  }

  const getVehicleInfo = (vehicleDetail: VehicleDetail) => {
    switch (vehicleDetail.catalogSource) {
      case 'DATAHUB':
        return `${vehicleDetail.dataHubVehicle?.name ?? ''} ${
          !isQueryBMM(vehicleDetail.vehicleIdentifiedBy) ? vehicleDetail.vehicleIdentifiedBy : ''
        }`;
      case 'IAM':
        return `${formatVehicleBrand(vehicleDetail.vehicleBrand) ?? vehicleDetail.iamVehicle?.name ?? ''} ${
          vehicleDetail.iamVehicle?.modelType ?? ''
        } ${!isQueryBMM(vehicleDetail.vehicleIdentifiedBy) ? vehicleDetail.vehicleIdentifiedBy : ''}`;
      default:
        return '';
    }
  };

  return (
    <SPopover
      afterVisibleChange={(visible) => setActiveSelecting(visible)}
      trigger={'click'}
      placement={'rightTop'}
      zIndex={200}
      content={
        <MarginBox>
          <Flex direction={'column'}>
            <AttachToVehicleHeader>
              <Flex minHeight={45} align={'center'}>
                <Text type="base">{t('cart.action.attachToVehicle', 'Attach reference to vehicle')}</Text>
              </Flex>
            </AttachToVehicleHeader>
            {vehicles.map((vehicle) => (
              <AttachToVehicleContent
                key={vehicle.vehicleDetail.vehicleKey}
                onClick={() => handleAttach(vehicle.vehicleDetail.vehicleKey)}
                align={'center'}
              >
                <Text type="base" color="grey35">
                  {getVehicleInfo(vehicle.vehicleDetail)}
                </Text>
              </AttachToVehicleContent>
            ))}
          </Flex>
        </MarginBox>
      }
    >
      <WithTooltip title={t('cart.action.link_ref_to_exist_vehicle', 'Link a ref to an existing vehicle')}>
        <Icon
          IconComponent={CarIcon}
          size={24}
          color={activeSelecting ? 'black' : 'grey'}
          data-cy={'cart-reference-link-to-vehicle-button'}
        />
      </WithTooltip>
    </SPopover>
  );
};

const ReferenceMark = ({
  vehicleKey,
  externalBasketId,
  referenceKey,
  referenceMark,
}: {
  vehicleKey: string | undefined;
  externalBasketId: string | undefined;
  referenceKey: ReferenceKey;
  referenceMark: string | undefined;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleRefMarkUpdate = useDebouncedCallback((value) => {
    dispatch(
      updateReferenceMarkRequest({
        basketReferenceType: getBasketReferenceType(vehicleKey, externalBasketId),
        vehicleKey,
        externalBasketId,
        referenceToUpdate: referenceKey,
        referenceMark: value,
      }),
    );
  }, 500);

  return (
    <Popover
      content={
        <RefInput
          initialValue={referenceMark}
          onChange={(value) => {
            handleRefMarkUpdate(value);
          }}
          placeholder={t('order.add_reference_mark', 'Type your reference mark here...')}
          bordered
          showCount
          maxLength={17}
          data-cy={`reference-mark-input-${referenceKey.referenceNumber}`}
        />
      }
      trigger={'click'}
      placement={'bottomLeft'}
      zIndex={200}
    >
      <PencilRoundButton
        bgColor={referenceMark ? 'selected' : 'grey95'}
        color={referenceMark ? 'white' : 'brand_black'}
        hoverColor={'brand_black'}
        tooltip={t('order.add_reference_mark.add_comment', 'Add comment')}
        data-cy={
          referenceMark
            ? `reference-add-comment-button-${referenceKey.referenceNumber}-filled`
            : `reference-add-comment-button-${referenceKey.referenceNumber}-empty`
        }
      />
    </Popover>
  );
};

const ProductPrice = ({ reference, currency }: { reference: ReferenceLocal; currency?: string }) => {
  const price = useSelector((state: RootState) => getPrice(state, reference.referenceNumber, reference.sellerNumber));
  const priceData = getData(price);
  return (
    <DataContainer data={price} Loading={() => <Spin size={'small'} />} ErrorState={() => <PriceErrorDisplay narrow />}>
      <Text type="base" data-cy={'cart-reference-common-price-pcl'}>
        {textFormatter.formatCurrency(priceData?.clientView?.recommendedPriceVatExcluded ?? 0, currency)}
      </Text>
    </DataContainer>
  );
};

const Discount = ({ referenceNumber, isUrgent }: { referenceNumber: string; isUrgent?: boolean }) => {
  const { t } = useTranslation();
  const price = useSelector((state: RootState) => getPrice(state, referenceNumber));
  const priceData = getData(price);

  return (
    <DataContainer
      data={price}
      Loading={() => <Spin size={'small'} />}
      ErrorState={() => (
        <ErrorWithLabel
          label={t('common.price.discount.backend_error', 'Discount temporarily unavailable, please try again later.')}
          narrow
        />
      )}
    >
      <Text type="base" data-cy={'cart-reference-discount'}>
        {textFormatter.formatPercentDecimal(
          Number(isUrgent ? priceData?.garageView?.discountRateUrgent : priceData?.garageView?.discountRate),
        )}
      </Text>
    </DataContainer>
  );
};

const PriceWithDiscounts = ({
  reference,
  currency,
  showTotal,
  isUrgent,
}: {
  reference: ReferenceLocal;
  currency?: string;
  showTotal?: boolean;
  isUrgent?: boolean;
}) => {
  const discount = useSelector((state: RootState) => getDiscount(state, reference.referenceNumber));
  const price = useSelector((state: RootState) => getPrice(state, reference.referenceNumber, reference.sellerNumber));
  const priceData = getData(price);

  const discountPrice = calculatePriceAfterDiscount(discount, priceData, false, isUrgent);
  const totalPrice = showTotal ? discountPrice * reference.quantity : discountPrice;

  return (
    <DataContainer data={price} Loading={() => <Spin size={'small'} />} ErrorState={() => <PriceErrorDisplay narrow />}>
      <ColoredText
        type={'text_dim'}
        color={getPromotionColor({
          staticType: undefined,
          referencePromotionType: discount?.promotionType,
        })}
        data-cy={'cart-reference-price'}
      >
        {textFormatter.formatCurrency(totalPrice, currency)}
      </ColoredText>
    </DataContainer>
  );
};

const ProductNameView = ({
  referenceType,
  name,
  brand,
  tireDimensions,
  catalogSource,
}: {
  referenceType: string;
  name: string;
  brand?: string;
  tireDimensions?: string;
  catalogSource?: CatalogSource;
}) => {
  if (referenceType === STANDARD) {
    return (
      <Text
        cursor={catalogSource !== IAM ? 'pointer' : 'initial'}
        type="base"
        bold
        transform={'capitalize'}
        hoverUnderLine={catalogSource !== IAM}
        data-cy={'cart-product-designation'}
      >
        {name}
      </Text>
    );
  }
  const tireBrand = tireBrandsData.find((tireBrand) => brand === tireBrand.brand);
  return (
    <TireNameContainer direction={'column'}>
      <WithTooltip title={`${tireBrand?.name ?? ''} ${tireDimensions ? `- ${tireDimensions}` : ''} ${name ?? ''}`}>
        <Flex direction={'row'}>
          <Text type="base" bold data-cy={'cart-product-designation-tires'}>
            {tireBrand?.name}
          </Text>
          <MarginBox mr={3} />
          {name && <Text type={'text'} ellipsis noWrap>{`- ${name}`}</Text>}
        </Flex>
        {tireDimensions && <Text type={'text'}>{tireDimensions}</Text>}
      </WithTooltip>
    </TireNameContainer>
  );
};

const UrgentDelivery = ({
  reference,
  vehicleKey,
  externalBasketId,
  currency,
}: {
  reference: ReferenceLocal;
  vehicleKey?: string;
  externalBasketId?: string;
  currency?: string;
}) => {
  const dispatch = useDispatch();
  const discount = useSelector((state: RootState) => getDiscount(state, reference.referenceNumber));
  const price = useSelector((state: RootState) => getPrice(state, reference.referenceNumber, reference.sellerNumber));
  const priceData = getData(price);
  const isReferenceNumberDepositMain = isRefNumberDepositMain(reference.referenceNumber);
  const depositRef = getDepositMainReferenceNumber(reference.referenceNumber);
  const referencesKeys = isReferenceNumberDepositMain
    ? [
        { referenceNumber: reference.referenceNumber, sellerNumber: undefined },
        {
          referenceNumber: depositRef,
          sellerNumber: undefined,
        },
      ]
    : [{ referenceNumber: reference.referenceNumber, sellerNumber: undefined }];

  const priceToDisplay = calculateUrgentDeliveryAddition(priceData, discount, true) * reference.quantity;
  return (
    <Flex>
      <Checkbox
        checked={reference.isUrgent}
        onChange={() => {
          dispatch(
            updateUrgencyFlagRequest({
              basketReferenceType: getBasketReferenceType(vehicleKey, externalBasketId),
              vehicleKey,
              externalBasketId,
              referenceToUpdate: {
                referenceNumber: reference.referenceNumber,
                sellerNumber: reference.sellerNumber,
              },
              isUrgent: !reference.isUrgent,
            }),
          );
          if (isReferenceNumberDepositMain) {
            dispatch(
              updateUrgencyFlagRequest({
                basketReferenceType: getBasketReferenceType(vehicleKey, externalBasketId),
                vehicleKey,
                externalBasketId,
                referenceToUpdate: {
                  referenceNumber: depositRef,
                  sellerNumber: reference.sellerNumber,
                },
                isUrgent: !reference.isUrgent,
              }),
            );
          }
          dispatch(
            updateReferenceUrgencyFlag({
              vehicleKey,
              externalBasketId,
              referencesToUpdate: referencesKeys,
              isUrgent: !reference.isUrgent,
            }),
          );
        }}
      />
      <Box width={18} />
      {textFormatter.formatCurrency(priceToDisplay, currency)}
    </Flex>
  );
};

const dataColumns = (
  dispatch: Dispatch,
  t: TFunction,
  vehicleKey: string | undefined,
  externalBasketId: string | undefined,
  markRemovedReferences: (numberOfReferences: number) => void,
  vehicles: VehicleLocal[],
  disabledRefs: string[],
  setDisableRefs: (ref: string) => void,
  unsetDisableRef: (ref: string) => void,
  currency?: string,
  catalogSource?: CatalogSource,
  userCountry?: string,
) => [
  {
    title: <>{externalBasketId ? <ExternalStrip /> : ''}</>,
    dataIndex: 'isSelected',
    key: 'rowSelection',
    render: function rowSelector(isSelected: boolean, reference: ReferenceLocal) {
      return (
        <>
          {!isRefNumberDeposit(reference.referenceNumber) ? (
            <>
              <DiscountCorner reference={reference.referenceNumber} type={'cart'} />
              <OverlayBox>
                <Selected
                  reference={reference}
                  isSelected={isSelected}
                  vehicleKey={vehicleKey}
                  externalBasketId={externalBasketId}
                  disabledRefs={disabledRefs}
                />
              </OverlayBox>
            </>
          ) : (
            <OverlayBox>
              <IconButton
                IconComponent={LinkIcon}
                boxSize={'xsm'}
                size={16}
                round
                hoverFill={'brand'}
                data-cy={'link-icon'}
              />
            </OverlayBox>
          )}
        </>
      );
    },
  },
  {
    title: t('cart.reference_table.product', 'Product'),
    dataIndex: 'product',
    key: 'product',
    render: function renderProductName(_text: string, reference: ReferenceLocal) {
      const redirectUrl =
        vehicleKey && catalogSource
          ? `${getCatalogSourceUrl(catalogSource)}/${vehicleKey}${ROUTER_CATALOG_VEHICLE}${ROUTER_PRODUCT}/${
              reference.referenceNumber
            }`
          : `${ROUTER_PRODUCT}/${reference.referenceNumber}`;
      if (catalogSource === IAM || reference.catalogSource === IAM) {
        return (
          <ProductNameView
            referenceType={reference.type}
            name={reference.name}
            brand={reference.brand}
            tireDimensions={reference.tireDimensions}
            catalogSource={IAM}
          />
        );
      }
      return (
        <Link to={redirectUrl}>
          <ProductNameView
            referenceType={reference.type}
            name={reference.name}
            brand={reference.brand}
            tireDimensions={reference.tireDimensions}
            catalogSource={catalogSource}
          />
        </Link>
      );
    },
  },
  {
    title: t('cart.reference_table.reference', 'Reference'),
    dataIndex: 'referenceNumber',
    key: 'referenceNumber',
    render: function renderReferenceNumber(
      referenceNumber: string,
      reference: ReferenceLocal,
      index: number,
      sellerNumber?: string,
    ) {
      const getAttachToVehicle = () => {
        if (index === 0) {
          return (
            <FirstHelpPopin streamId={ROUTER_CART} popinId={`${ROUTER_CART}_link_to_veh`} placement={'right'}>
              <AttachToVehicle vehicles={vehicles} reference={{ referenceNumber, sellerNumber }} />
            </FirstHelpPopin>
          );
        }
        return <AttachToVehicle vehicles={vehicles} reference={{ referenceNumber, sellerNumber }} />;
      };

      return (
        <div id={`cart-reference-row-${referenceNumber}`}>
          <Flex align={'center'}>
            <Text type="base">{referenceNumber}</Text>
            {!reference.isCompatible && !externalBasketId && (
              <>
                <MarginBox ml={15} />
                <WithTooltip
                  title={t('estimate.reference.noncompatible', 'Not compatible with selected vehicle')}
                  placement={'top'}
                >
                  <TooltipContent data-cy={'incompatible-vehicle-tooltip'}>
                    <Flex size={0} align={'center'} justify={'center'}>
                      <Icon IconComponent={UnlinkIcon} size={16} color={'black'} display={'inline'} />
                    </Flex>
                  </TooltipContent>
                </WithTooltip>
              </>
            )}
            {!vehicleKey && !externalBasketId && vehicles.length > 0 && (
              <Flex>
                <Box width={15} />
                {getAttachToVehicle()}
              </Flex>
            )}
            {(isRefNumberDeposit(referenceNumber) || isRefNumberDepositMain(referenceNumber)) && (
              <>
                <MarginBox ml={15} />
                <WithTooltip title={t('cart.info.deposit.reference', 'Deposit reference')}>
                  <Icon IconComponent={DepositIcon} size={24} color={'black'} data-cy={'deposit-icon'} />
                </WithTooltip>
              </>
            )}
          </Flex>
        </div>
      );
    },
  },
  {
    title: t('cart.reference_table.stock', 'Stock'),
    dataIndex: 'referenceNumber',
    key: 'stock',
    render: function renderStock(referenceNumber: string, reference: ReferenceLocal) {
      if (isRefNumberDeposit(referenceNumber)) {
        return <>-</>;
      }
      return (
        <Stock
          referenceNumber={referenceNumber}
          reference={reference}
          vehicleKey={vehicleKey}
          externalBasketId={externalBasketId}
          disabledRefs={disabledRefs}
          setDisableRefs={setDisableRefs}
          unsetDisableRef={unsetDisableRef}
          sellerNumber={reference.sellerNumber}
        />
      );
    },
  },
  {
    title: t('common.price.pcl', 'PCL'),
    dataIndex: 'pclPrice',
    key: 'pclPrice',
    render: function renderPrice(_text: string, reference: ReferenceLocal) {
      return <ProductPrice reference={reference} currency={currency} />;
    },
  },
  {
    title: t('cart.reference_table.discount', 'Discount'),
    dataIndex: 'discount',
    key: 'discount',
    render: function renderPrice(_text: string, reference: ReferenceLocal) {
      return <Discount referenceNumber={reference?.referenceNumber} isUrgent={reference.isUrgent} />;
    },
  },
  {
    title: t('common.price.pnm', 'Price PNM'),
    dataIndex: 'pnmPrice',
    key: 'pnmPrice',
    width: 90,
    render: function renderPrice(_text: string, reference: ReferenceLocal) {
      return <PriceWithDiscounts reference={reference} currency={currency} isUrgent={reference.isUrgent} />;
    },
  },
  {
    title: t('order.urgent_delivery', 'Urgent delivery'),
    dataIndex: 'urgentDelivery',
    key: 'urgentDelivery',
    render: function renderUrgentDelivery(_isUrgent: boolean | undefined, ref: ReferenceLocal) {
      return (
        <UrgentDelivery
          reference={ref}
          vehicleKey={vehicleKey}
          externalBasketId={externalBasketId}
          currency={currency}
        />
      );
    },
  },
  {
    title: t('common.quantity', 'Quantity'),
    dataIndex: 'quantity',
    key: 'quantity',
    render: function renderQuantity(currentQuantity: number, ref: ReferenceLocal) {
      return (
        <Flex justify={'center'}>
          <BasketQuantityInput
            currentQuantity={currentQuantity}
            vehicleKey={vehicleKey}
            externalBasketId={externalBasketId}
            reference={ref}
            markRemovedReferences={markRemovedReferences}
          />
        </Flex>
      );
    },
  },
  {
    title: t('common.price.VAT_excl_price', 'VAT Excl. Price'),
    dataIndex: 'priceVatExcluded',
    key: 'vatExclPrice',
    render: function renderPrice(_text: string, reference: ReferenceLocal) {
      return <PriceWithDiscounts reference={reference} currency={currency} showTotal isUrgent={reference.isUrgent} />;
    },
  },
  {
    title: '',
    dataIndex: 'referenceNumber',
    key: 'referenceActions',
    render: function renderActions(referenceNumber: string, reference: ReferenceLocal, index: number) {
      if (isRefNumberDeposit(referenceNumber)) {
        return <></>;
      }
      const getReferenceMark = () => {
        if (
          (vehicles.length > 0 && vehicles[0].vehicleDetail.vehicleKey === vehicleKey && index === 0) ||
          (vehicles.length === 0 && !vehicleKey && index === 0)
        ) {
          return (
            <FirstHelpPopin streamId={ROUTER_CART} popinId={`${ROUTER_CART}_add_comment`} placement={'bottom'}>
              <ReferenceMark
                vehicleKey={vehicleKey}
                referenceKey={{ referenceNumber, sellerNumber: reference.sellerNumber }}
                externalBasketId={externalBasketId}
                referenceMark={reference.note}
              />
            </FirstHelpPopin>
          );
        }
        return (
          <ReferenceMark
            vehicleKey={vehicleKey}
            externalBasketId={externalBasketId}
            referenceKey={{ referenceNumber, sellerNumber: reference.sellerNumber }}
            referenceMark={reference.note}
          />
        );
      };

      return (
        <Flex>
          <Box width={33}>{userCountry !== 'ES' && userCountry !== 'IT' && getReferenceMark()}</Box>
          <Box width={12} />
          <TrashButton
            referenceNumber={referenceNumber}
            vehicleKey={vehicleKey}
            externalBasketId={externalBasketId}
            markRemovedReferences={markRemovedReferences}
            sellerNumber={reference.sellerNumber}
          />
        </Flex>
      );
    },
  },
];

interface CartReferenceTableProps {
  vehicleKey?: string;
  externalBasketId?: string;
  markRemovedReferences: (numberOfReferences: number) => void;
  disabledRefs: string[];
  highlightedReference: ReferenceKey | undefined;
  setDisableRefs: (ref: string) => void;
  unsetDisableRef: (ref: string) => void;
}

const CartReferenceTable = ({
  vehicleKey,
  externalBasketId,
  markRemovedReferences,
  disabledRefs,
  highlightedReference,
  setDisableRefs,
  unsetDisableRef,
}: CartReferenceTableProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const commercialLink = useSelector(getUserCommercialLink);
  const vehicles = useSelector(getBasketVehicles);
  const otherSection = useSelector(getBasketOtherSection);
  const externalBaskets = useSelector(getBasketExternalSection);
  const currency = useSelector(getCurrency);
  const vehicleReferences = vehicles?.find((vehicle) => vehicle?.vehicleDetail?.vehicleKey === vehicleKey)?.references;
  const vehicleCatalogSource = vehicles?.find((vehicle) => vehicle?.vehicleDetail?.vehicleKey === vehicleKey)
    ?.vehicleDetail?.catalogSource;
  const otherReferences = otherSection?.references;
  const externalReferences = externalBaskets?.find((extBasket) => extBasket?.externalBasketId === externalBasketId)
    ?.references;
  const userCountry = useSelector(getUserCountry);

  const columns = dataColumns(
    dispatch,
    t,
    vehicleKey,
    externalBasketId,
    markRemovedReferences,
    vehicles,
    disabledRefs,
    setDisableRefs,
    unsetDisableRef,
    currency,
    vehicleCatalogSource,
    userCountry,
  );

  return (
    <STable<ReferenceLocal>
      rowKey={(row) => row.referenceNumber}
      columns={
        commercialLink !== null && commercialLink.canPlaceUrgentOrders
          ? columns
          : columns.filter((column) => column.dataIndex !== 'urgentDelivery')
      }
      dataSource={
        vehicleKey
          ? vehicleReferences?.map((vehicleRef) => ({ ...vehicleRef, key: vehicleRef.referenceNumber }))
          : externalBasketId
          ? externalReferences?.map((extRef) => ({ ...extRef, key: extRef.referenceNumber }))
          : otherReferences?.map((otherRef) => ({ ...otherRef, key: otherRef.referenceNumber }))
      }
      rowClassName={(record) =>
        `${
          disabledRefs.includes(record.referenceNumber) || isRefNumberDeposit(record.referenceNumber)
            ? 'disabled-row'
            : ''
        } ${
          record.referenceNumber === highlightedReference?.referenceNumber &&
          record.sellerNumber === highlightedReference?.sellerNumber &&
          'highlighted-row'
        }`
      }
      pagination={false}
      data-cy={'reference-table'}
      onRow={(record) =>
        ({
          'data-cy': isRefNumberDeposit(record.referenceNumber)
            ? `deposit-row-${record.referenceNumber}-linked-to-${getMainReferenceFromDeposit(record.referenceNumber)}`
            : `reference-row-${record.referenceNumber}`,
        } as React.HTMLAttributes<HTMLElement>)
      }
    />
  );
};

export default CartReferenceTable;

// Export for testing
export { Selected, Stock };
